/**
 * Dynamic Color Generation System
 * 
 * Generates consistent colors for themes, statuses, and other categorical data.
 * Uses a hash-based approach so the same string always produces the same color.
 * Preferred colors are used when available, otherwise colors are auto-generated.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// THEME ICONS (Lucide icon names for each theme)
// ═══════════════════════════════════════════════════════════════════════════════

export const THEME_ICONS = {
  "Rural Upliftment":                    "Wheat",       // Agriculture/farming
  "Education":                           "GraduationCap",
  "Health":                              "Heart",       // or HeartPulse
  "Cancer Care":                         "Ribbon",      // Cancer awareness ribbon
  "Building Institutions":               "Building",    // or Landmark
  "Nutrition":                           "Apple",       // or Utensils
  "Water and Sanitation":                "Droplets",    // Water drops
  "Arts, Culture and Creative Economies": "Palette",    // Art palette
  "Urban Poverty Alleviation":           "Home",        // or Building2
  "Others":                              "MoreHorizontal",
  // Abbreviated/alternate names
  "RUP":                                 "Wheat",
  "WASH":                                "Droplets",
  "Institutions":                        "Building",
  "Arts & Culture":                      "Palette",
};

// ═══════════════════════════════════════════════════════════════════════════════
// PREFERRED COLORS (manually curated for known values)
// ═══════════════════════════════════════════════════════════════════════════════

export const PREFERRED_THEME_COLORS = {
  "Rural Upliftment":                    { bg: "bg-amber-100", text: "text-amber-800", dot: "#D97706" },
  "Education":                           { bg: "bg-blue-100", text: "text-blue-700", dot: "#2563EB" },
  "Health":                              { bg: "bg-emerald-100", text: "text-emerald-700", dot: "#059669" },
  "Cancer Care":                         { bg: "bg-rose-100", text: "text-rose-700", dot: "#E11D48" },
  "Building Institutions":               { bg: "bg-violet-100", text: "text-violet-700", dot: "#7C3AED" },
  "Nutrition":                           { bg: "bg-orange-100", text: "text-orange-700", dot: "#EA580C" },
  "Water and Sanitation":                { bg: "bg-cyan-100", text: "text-cyan-700", dot: "#0891B2" },
  "Arts, Culture and Creative Economies": { bg: "bg-pink-100", text: "text-pink-700", dot: "#DB2777" },
  "Urban Poverty Alleviation":           { bg: "bg-indigo-100", text: "text-indigo-700", dot: "#4338CA" },
  "Others":                              { bg: "bg-slate-100", text: "text-slate-600", dot: "#94A3B8" },
  // Legacy/abbreviated names
  "RUP":           { bg: "bg-amber-100", text: "text-amber-800", dot: "#D97706" },
  "WASH":          { bg: "bg-cyan-100", text: "text-cyan-700", dot: "#0891B2" },
  "Institutions":  { bg: "bg-violet-100", text: "text-violet-700", dot: "#7C3AED" },
  "Arts & Culture": { bg: "bg-pink-100", text: "text-pink-700", dot: "#DB2777" },
};

export const PREFERRED_STATUS_COLORS = {
  // Payment statuses
  "Paid":              { bg: "bg-green-100", text: "text-green-700", hex: "#16A34A" },
  "paid":              { bg: "bg-green-100", text: "text-green-700", hex: "#16A34A" },
  "FM Review":         { bg: "bg-amber-100", text: "text-amber-700", hex: "#F59E0B" },
  "fm review":         { bg: "bg-amber-100", text: "text-amber-700", hex: "#F59E0B" },
  "Ready for Payment": { bg: "bg-sky-100", text: "text-sky-700", hex: "#0EA5E9" },
  "ready for payment": { bg: "bg-sky-100", text: "text-sky-700", hex: "#0EA5E9" },
  "Pending":           { bg: "bg-yellow-100", text: "text-yellow-700", hex: "#EAB308" },
  "pending":           { bg: "bg-yellow-100", text: "text-yellow-700", hex: "#EAB308" },
  "Approved":          { bg: "bg-emerald-100", text: "text-emerald-700", hex: "#10B981" },
  "approved":          { bg: "bg-emerald-100", text: "text-emerald-700", hex: "#10B981" },
  "Rejected":          { bg: "bg-red-100", text: "text-red-700", hex: "#EF4444" },
  "rejected":          { bg: "bg-red-100", text: "text-red-700", hex: "#EF4444" },
  "Cancelled":         { bg: "bg-slate-100", text: "text-slate-600", hex: "#64748B" },
  "cancelled":         { bg: "bg-slate-100", text: "text-slate-600", hex: "#64748B" },
  "Canceled":          { bg: "bg-slate-100", text: "text-slate-600", hex: "#64748B" },
  // Grant statuses
  "Active":            { bg: "bg-green-100", text: "text-green-700", hex: "#16A34A" },
  "active":            { bg: "bg-green-100", text: "text-green-700", hex: "#16A34A" },
  "Closed":            { bg: "bg-slate-100", text: "text-slate-600", hex: "#64748B" },
  "closed":            { bg: "bg-slate-100", text: "text-slate-600", hex: "#64748B" },
  "Completed":         { bg: "bg-blue-100", text: "text-blue-700", hex: "#3B82F6" },
  "completed":         { bg: "bg-blue-100", text: "text-blue-700", hex: "#3B82F6" },
  // New payment workflow statuses (from Snowflake)
  "CEO Approval":      { bg: "bg-violet-100", text: "text-violet-700", hex: "#7C3AED" },
  "CFO Review":        { bg: "bg-purple-100", text: "text-purple-700", hex: "#9333EA" },
  "HoP Review":        { bg: "bg-indigo-100", text: "text-indigo-700", hex: "#4F46E5" },
  "Trustee Approval":  { bg: "bg-pink-100", text: "text-pink-700", hex: "#DB2777" },
  "Secondary Review - TH": { bg: "bg-fuchsia-100", text: "text-fuchsia-700", hex: "#C026D3" },
  "Scheduled":         { bg: "bg-cyan-100", text: "text-cyan-700", hex: "#0891B2" },
  "Refunded":          { bg: "bg-orange-100", text: "text-orange-700", hex: "#EA580C" },
};

// ═══════════════════════════════════════════════════════════════════════════════
// AUTO-GENERATED COLOR PALETTE (for unknown values)
// ═══════════════════════════════════════════════════════════════════════════════

// Tailwind color palette for auto-generation (avoiding colors already used in preferred)
const AUTO_COLOR_PALETTE = [
  { bg: "bg-teal-100", text: "text-teal-700", hex: "#0D9488" },
  { bg: "bg-lime-100", text: "text-lime-700", hex: "#65A30D" },
  { bg: "bg-fuchsia-100", text: "text-fuchsia-700", hex: "#C026D3" },
  { bg: "bg-red-100", text: "text-red-700", hex: "#DC2626" },
  { bg: "bg-yellow-100", text: "text-yellow-700", hex: "#CA8A04" },
  { bg: "bg-purple-100", text: "text-purple-700", hex: "#9333EA" },
  { bg: "bg-green-100", text: "text-green-700", hex: "#16A34A" },
  { bg: "bg-sky-100", text: "text-sky-700", hex: "#0284C7" },
  { bg: "bg-stone-100", text: "text-stone-700", hex: "#78716C" },
  { bg: "bg-zinc-100", text: "text-zinc-700", hex: "#71717A" },
];

/**
 * Simple string hash function for consistent color assignment
 */
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Get color for a theme - uses preferred if available, otherwise auto-generates
 */
export function getThemeColor(theme) {
  if (!theme) return { bg: "bg-slate-100", text: "text-slate-600", dot: "#94A3B8" };
  
  // Check preferred colors first
  if (PREFERRED_THEME_COLORS[theme]) {
    return PREFERRED_THEME_COLORS[theme];
  }
  
  // Auto-generate consistent color based on theme name hash
  const idx = hashString(theme) % AUTO_COLOR_PALETTE.length;
  const color = AUTO_COLOR_PALETTE[idx];
  return { bg: color.bg, text: color.text, dot: color.hex };
}

/**
 * Get Tailwind classes for theme chip
 */
export function getThemeClasses(theme) {
  const color = getThemeColor(theme);
  return `${color.bg} ${color.text}`;
}

/**
 * Get hex color for theme dot/chart
 */
export function getThemeDotColor(theme) {
  return getThemeColor(theme).dot;
}

/**
 * Get color for a status - uses preferred if available, otherwise auto-generates
 */
export function getStatusColor(status) {
  if (!status) return { bg: "bg-slate-100", text: "text-slate-600", hex: "#94A3B8" };
  
  // Check preferred colors first
  if (PREFERRED_STATUS_COLORS[status]) {
    return PREFERRED_STATUS_COLORS[status];
  }
  
  // Auto-generate consistent color based on status name hash
  const idx = hashString(status) % AUTO_COLOR_PALETTE.length;
  const color = AUTO_COLOR_PALETTE[idx];
  return { bg: color.bg, text: color.text, hex: color.hex };
}

/**
 * Get hex color for status (used in charts)
 */
export function getStatusHexColor(status) {
  return getStatusColor(status).hex;
}

/**
 * Get Tailwind classes for status badge
 */
export function getStatusClasses(status) {
  const color = getStatusColor(status);
  return `${color.bg} ${color.text}`;
}

/**
 * Check if a theme has a preferred (manually curated) color
 */
export function isPreferredTheme(theme) {
  return theme in PREFERRED_THEME_COLORS;
}

/**
 * Check if a status has a preferred (manually curated) color  
 */
export function isPreferredStatus(status) {
  return status in PREFERRED_STATUS_COLORS;
}

/**
 * Get the icon name for a theme (Lucide icon name)
 */
export function getThemeIconName(theme) {
  if (!theme) return "MoreHorizontal";
  return THEME_ICONS[theme] || "MoreHorizontal";
}
